"use client";

import { FC, PropsWithChildren, useEffect } from "react";
import { ThemeProvider } from "next-themes";
import { usePathname } from "next/navigation";
import { QueryClientProvider } from "@tanstack/react-query";
import axios from "axios";
import { getQueryClient } from "@/app/getQueryClient";
import { useConsent } from "@/lib/consent";
import { startAnalytics } from "@/lib/analytics";

// Identify this site to the API on every request. The generated API client
// uses this shared axios instance, so all partner-scoped calls (e.g. the
// contact/waitlist forms) inherit the X-Partner-Id header.
if (process.env.NEXT_PUBLIC_PARTNER_ID) {
  axios.defaults.headers.common["X-Partner-Id"] =
    process.env.NEXT_PUBLIC_PARTNER_ID;
}

// The API derives email-template locale from Accept-Language; send a clean
// locale so it resolves a real template (the browser default "en-US,en;q=0.9"
// is used verbatim by the backend and fails to match a template name).
axios.defaults.headers.common["Accept-Language"] = "en";

export const Providers: FC<PropsWithChildren> = ({ children }) => {
  const queryClient = getQueryClient();
  const pathname = usePathname();
  // user_source is attribution, so it waits for analytics consent.
  const analyticsConsent = useConsent("analytics");

  // The World Cup waitlist is designed dark-only; force dark on that route.
  const forcedTheme = pathname?.startsWith("/world-cup-2026")
    ? "dark"
    : undefined;

  // PostHog (user-retention step 05): loads only after analytics consent.
  useEffect(() => startAnalytics(), []);

  useEffect(() => {
    if (!analyticsConsent) return;

    const referrer = document.referrer;
    function setCookie(name: string, value: string, days: number) {
      let expires = "";
      if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = "; expires=" + date.toUTCString();
      }
      document.cookie =
        name +
        "=" +
        encodeURIComponent(value) +
        expires +
        "; path=/; domain=.football-genie.com";
    }

    if (referrer && !document.cookie.includes("user_source")) {
      const sourceDomain = new URL(referrer).hostname;

      if (!sourceDomain.includes("football-genie.com")) {
        setCookie("user_source", referrer, 30); // Store for 30 days
      }
    }
  }, [analyticsConsent]);

  return (
    <ThemeProvider attribute="class" forcedTheme={forcedTheme}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </ThemeProvider>
  );
};
